import React from 'react';
import { Box, Typography, Chip } from '@mui/material';

interface FormattedMessageProps {
  content: string;
  role: 'user' | 'assistant';
}

/**
 * Parse markdown-style formatting and render with MUI components
 */
const FormattedMessage: React.FC<FormattedMessageProps> = ({ content, role }) => {
  // Parse the content into sections
  const parseContent = (text: string) => {
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];
    let inList = false;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <Box component="ul" key={`list-${elements.length}`} sx={{ pl: 2, mb: 1, mt: 0.5 }}>
            {currentList.map((item, idx) => (
              <Typography component="li" key={idx} variant="body2" sx={{ mb: 0.5, lineHeight: 1.5 }}>
                {formatInlineStyles(item)}
              </Typography>
            ))}
          </Box>
        );
        currentList = [];
        inList = false;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Skip empty lines
      if (!trimmedLine) {
        flushList();
        return;
      }

      // Check for main headers (e.g., "**1. Preventive Strategies**")
      const mainHeaderMatch = trimmedLine.match(/^\*\*\d+\.\s+(.+?)\*\*$/);
      if (mainHeaderMatch) {
        flushList();
        elements.push(
          <Chip
            key={`header-${index}`}
            label={mainHeaderMatch[1]}
            color="primary"
            size="small"
            sx={{
              mb: 1,
              mt: 2,
              fontWeight: 'bold',
              fontSize: '0.85rem',
              height: 28,
              borderRadius: 1,
            }}
          />
        );
        return;
      }

      // Check for sub-headers (e.g., "**Follow-Up Question:**")
      const subHeaderMatch = trimmedLine.match(/^\*\*(.+?):\*\*$/);
      if (subHeaderMatch) {
        flushList();
        elements.push(
          <Typography
            key={`subheader-${index}`}
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              mt: 1.5,
              mb: 0.5,
              color: 'text.primary',
            }}
          >
            {subHeaderMatch[1]}
          </Typography>
        );
        return;
      }

      // Check for list items (e.g., "- **Pre-correct:** ...")
      const listItemMatch = trimmedLine.match(/^[-•]\s+(.+)$/);
      if (listItemMatch) {
        inList = true;
        currentList.push(listItemMatch[1]);
        return;
      }

      // Regular paragraph
      if (inList) {
        flushList();
      }

      elements.push(
        <Typography
          key={`para-${index}`}
          variant="body2"
          sx={{ mb: 1, lineHeight: 1.6 }}
        >
          {formatInlineStyles(trimmedLine)}
        </Typography>
      );
    });

    flushList();
    return elements;
  };

  /**
   * Format inline styles like **bold** and *italic*
   */
  const formatInlineStyles = (text: string): React.ReactNode => {
    // Split by bold markers
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, idx) => {
      // Check for bold text
      const boldMatch = part.match(/^\*\*(.+?)\*\*$/);
      if (boldMatch) {
        // Check if this is a tag-like bold (ends with colon)
        const tagText = boldMatch[1];
        if (tagText.includes(':')) {
          return (
            <Box
              component="span"
              key={idx}
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                px: 0.5,
                py: 0.2,
                borderRadius: 0.5,
                fontSize: '0.85rem',
              }}
            >
              {tagText}
            </Box>
          );
        }
        return (
          <Box component="strong" key={idx} sx={{ fontWeight: 'bold' }}>
            {tagText}
          </Box>
        );
      }
      return part;
    });
  };

  if (role === 'user') {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: '#f7f7f8',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2">{content}</Typography>
      </Box>
    );
  }

  // Assistant message with formatted content
  return (
    <Box
      sx={{
        p: 2.5,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      {parseContent(content)}
    </Box>
  );
};

export default FormattedMessage;
